import React, { createContext, useState, useEffect, useContext, useCallback } from "react";
import {
  RadixDappToolkit,
  DataRequestBuilder,
} from "@radixdlt/radix-dapp-toolkit";
import { RADIX_NETWORK } from "../constants";

const SELECTED_ACCOUNT_KEY = 'radix_selected_account_address';

// Create the context
export const RadixConnectContext = createContext(null);

// Hook to use the Radix Connect context
export const useRadixConnect = () => {
  const context = useContext(RadixConnectContext);
  if (!context) {
    throw new Error(
      "useRadixConnect must be used within a RadixConnectProvider"
    );
  }
  return context;
};

// Provider component
export const RadixConnectProvider = ({ children, dAppDefinitionAddress }) => {
  const [rdt, setRdt] = useState(null);
  const [connected, setConnected] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccountState] = useState(null);
  // Removed userBadgeId state

  // Wrap setSelectedAccount to persist the choice in localStorage
  const setSelectedAccount = useCallback((account) => {
    setSelectedAccountState(account);
    try {
      if (account?.address) {
        localStorage.setItem(SELECTED_ACCOUNT_KEY, account.address);
      } else {
        localStorage.removeItem(SELECTED_ACCOUNT_KEY);
      }
    } catch (e) {
      // localStorage may be unavailable (e.g. storage disabled)
    }
  }, []);

  // Initialize the Radix dApp Toolkit
  useEffect(() => {
    const toolkit = RadixDappToolkit({
      dAppDefinitionAddress,
      networkId: RADIX_NETWORK,
      applicationName: "Reddicks Games",
      applicationVersion: "1.0.0",
    });

    // Set the button theme to dark mode
    toolkit.buttonApi.setTheme("black");
    toolkit.buttonApi.setMode("dark");

    // Configure the data request to include accounts (original state)
    toolkit.walletApi.setRequestData(DataRequestBuilder.accounts().atLeast(1));

    // Handle connect responses with improved error logging
    toolkit.walletApi.provideConnectResponseCallback((result) => {
      if (result.isErr()) {
        console.error("Connect error:", result.error);
      } else {
        setConnected(true);
      }
    });

    // Log when toolkit is initialized
    setRdt(toolkit);

    // Clean up on unmount
    return () => {
      toolkit.destroy();
    };
  }, [dAppDefinitionAddress]);

  // Handle connect/disconnect events
  useEffect(() => {
    if (!rdt) return;

    const handleConnect = () => {
      setConnected(true);
    };

    const handleDisconnect = () => {
      setConnected(false);
      setAccounts([]);
      setSelectedAccountState(null);
      try { localStorage.removeItem(SELECTED_ACCOUNT_KEY); } catch (e) { }
      // Removed setUserBadgeId(null)
    };

    // Add event listeners
    document.addEventListener("radix-connect-button:onConnect", handleConnect);
    document.addEventListener(
      "radix-connect-button:onDisconnect",
      handleDisconnect
    );

    // Check if the button is already in a connected state
    setTimeout(() => {
      const connectButton = document.querySelector("radix-connect-button");
      if (
        connectButton &&
        connectButton.getAttribute("state") === "connected"
      ) {
        setConnected(true);
      }
    }, 500);

    // Subscribe to wallet data changes with better logging
    const subscription = rdt.walletApi.walletData$.subscribe((walletData) => {
      // Check if we're connected based on wallet data
      if (walletData && walletData.accounts && walletData.accounts.length > 0) {
        setConnected(true);
        setAccounts(walletData.accounts);

        if (!selectedAccount) {
          // Try to restore previously selected account from localStorage
          let savedAddress = null;
          try { savedAddress = localStorage.getItem(SELECTED_ACCOUNT_KEY); } catch (e) { }
          const savedAccount = savedAddress
            ? walletData.accounts.find(a => a.address === savedAddress)
            : null;
          setSelectedAccountState(savedAccount || walletData.accounts[0]);
        }
        // Removed nonFungibleResources parsing logic
      } else {
        setConnected(false);
        setAccounts([]);
        setSelectedAccountState(null);
        // Removed setUserBadgeId(null)
      }
    });

    // Check initial connection state
    const initialWalletData = rdt.walletApi.getWalletData();
    if (
      initialWalletData &&
      initialWalletData.accounts &&
      initialWalletData.accounts.length > 0
    ) {
      setConnected(true);
      setAccounts(initialWalletData.accounts);
      if (!selectedAccount) {
        // Try to restore previously selected account from localStorage
        let savedAddress = null;
        try { savedAddress = localStorage.getItem(SELECTED_ACCOUNT_KEY); } catch (e) { }
        const savedAccount = savedAddress
          ? initialWalletData.accounts.find(a => a.address === savedAddress)
          : null;
        setSelectedAccountState(savedAccount || initialWalletData.accounts[0]);
      }
      // Removed nonFungibleResources parsing logic
    } else {
      setConnected(false);
      setAccounts([]);
      setSelectedAccountState(null);
      // Removed setUserBadgeId(null)
    }

    return () => {
      document.removeEventListener(
        "radix-connect-button:onConnect",
        handleConnect
      );
      document.removeEventListener(
        "radix-connect-button:onDisconnect",
        handleDisconnect
      );
      subscription.unsubscribe();
    };
  }, [rdt, selectedAccount]);

  // Function to request account sharing update
  const updateAccountSharing = async () => {
    if (!rdt) return;

    try {
      const result = await rdt.walletApi.sendRequest();
      if (result.isErr()) {
        console.error("Error updating account sharing:", result.error);
        return;
      }
    } catch (error) {
      console.error("Error sending request:", error);
    }
  };

  // Context value
  const contextValue = {
    rdt,
    connected,
    accounts,
    selectedAccount,
    setSelectedAccount,
    updateAccountSharing,
  };

  return (
    <RadixConnectContext.Provider value={contextValue}>
      {children}
    </RadixConnectContext.Provider>
  );
};
